import React, { useState } from 'react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import {
  ChevronDown, ChevronRight, Plus, Trash2,
  FileText, BookOpen, ExternalLink, FolderTree
} from 'lucide-react';

const uid = () => Math.random().toString(36).substring(2, 10);

/* ═══════════════════════════════════════════
   PM ENTRY CHILD TYPES
   ═══════════════════════════════════════════ */

const DmRefEditor = ({ item, onChange, onDelete, dmlEntries, dirFiles }) => {
  const allDmcOptions = [
    ...(dmlEntries || []).filter(e => (e.entry_type || 'dm') === 'dm').map(e => ({ label: `${e.dmc_formatted} — ${e.title}`, dmc: e.dmc_formatted, title: e.title })),
    ...(dirFiles || []).filter(f => f.name.toUpperCase().startsWith('DMC-')).map(f => {
      const base = f.name.replace(/\.xml$/i, '');
      return { label: base, dmc: base, title: '' };
    }),
  ];

  const handleQuickSelect = (dmc) => {
    // DMC format: DMC-MIC-SDC-SYS-SUBSUBSUB-ASSY-DISASSY(VAR)-INFO(VAR)-LOC (8 segments after DMC)
    const match = dmc.match(/DMC-([^-_]+)-([^-_]+)-([^-_]+)-([^-_]+)-([^-_]+)-([^-_]+)-([^-_]+)-([^-_.]+)/i);
    if (match) {
      const entry = allDmcOptions.find(o => o.dmc === dmc);
      const subSeg = match[4];
      const disassySeg = match[6];
      const infoSeg = match[7];
      const disassyM = disassySeg.match(/^(\d+)([A-Za-z]*)$/) || [null, disassySeg, ''];
      const infoM = infoSeg.match(/^([0-9A-Za-z]{3})([A-Za-z]*)$/) || [null, infoSeg, ''];
      onChange({
        ...item,
        modelIdentCode: match[1], systemDiffCode: match[2], systemCode: match[3],
        subSystemCode: subSeg.charAt(0) || '', subSubSystemCode: subSeg.charAt(1) || '',
        assyCode: match[5],
        disassyCode: disassyM[1],
        disassyCodeVariant: disassyM[2] || '',
        infoCode: infoM[1],
        infoCodeVariant: infoM[2] || '',
        itemLocationCode: match[8],
        dmTitle: entry?.title || item.dmTitle || '',
      });
    }
  };

  return (
    <div className="flex items-start gap-2 group" data-testid={`pm-dmref-${item.id}`}>
      <FileText className="w-4 h-4 text-blue-500 mt-2.5 flex-shrink-0" />
      <div className="flex-1 space-y-1.5">
        <div className="flex items-center gap-2">
          <span className="text-[10px] font-bold uppercase tracking-wider text-blue-600">dmRef</span>
          {allDmcOptions.length > 0 && (
            <select
              className="text-xs border border-slate-200 rounded px-1.5 py-0.5 bg-white max-w-xs"
              value=""
              onChange={(e) => { if (e.target.value) handleQuickSelect(e.target.value); }}
            >
              <option value="">Select from DML / directory...</option>
              {allDmcOptions.map((o, i) => (
                <option key={i} value={o.dmc}>{o.label}</option>
              ))}
            </select>
          )}
        </div>
        <div className="grid grid-cols-6 gap-1.5">
          <Input className="h-7 text-xs font-mono" placeholder="modelIdent" value={item.modelIdentCode || ''}
            onChange={e => onChange({ ...item, modelIdentCode: e.target.value })} />
          <Input className="h-7 text-xs font-mono" placeholder="sysDiff" value={item.systemDiffCode || ''}
            onChange={e => onChange({ ...item, systemDiffCode: e.target.value })} />
          <Input className="h-7 text-xs font-mono" placeholder="sys" value={item.systemCode || ''}
            onChange={e => onChange({ ...item, systemCode: e.target.value })} />
          <Input className="h-7 text-xs font-mono" placeholder="subSys" value={item.subSystemCode || ''}
            onChange={e => onChange({ ...item, subSystemCode: e.target.value })} />
          <Input className="h-7 text-xs font-mono" placeholder="subSubSys" value={item.subSubSystemCode || ''}
            onChange={e => onChange({ ...item, subSubSystemCode: e.target.value })} />
          <Input className="h-7 text-xs font-mono" placeholder="assy" value={item.assyCode || ''}
            onChange={e => onChange({ ...item, assyCode: e.target.value })} />
        </div>
        <div className="grid grid-cols-5 gap-1.5">
          <Input className="h-7 text-xs font-mono" placeholder="disassy" value={item.disassyCode || ''}
            onChange={e => onChange({ ...item, disassyCode: e.target.value })} />
          <Input className="h-7 text-xs font-mono" placeholder="var" value={item.disassyCodeVariant || ''}
            onChange={e => onChange({ ...item, disassyCodeVariant: e.target.value })} />
          <Input className="h-7 text-xs font-mono" placeholder="info" value={item.infoCode || ''}
            onChange={e => onChange({ ...item, infoCode: e.target.value })} />
          <Input className="h-7 text-xs font-mono" placeholder="var" value={item.infoCodeVariant || ''}
            onChange={e => onChange({ ...item, infoCodeVariant: e.target.value })} />
          <Input className="h-7 text-xs font-mono" placeholder="loc" value={item.itemLocationCode || ''}
            onChange={e => onChange({ ...item, itemLocationCode: e.target.value })} />
        </div>
        <Input className="h-7 text-xs" placeholder="DM Title (optional)" value={item.dmTitle || ''}
          onChange={e => onChange({ ...item, dmTitle: e.target.value })} />
      </div>
      <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-red-400 hover:text-red-600 opacity-0 group-hover:opacity-100"
        onClick={onDelete}><Trash2 className="w-3.5 h-3.5" /></Button>
    </div>
  );
};

const PmRefEditor = ({ item, onChange, onDelete, dmlEntries = [], dirFiles = [] }) => {
  const allPmcOptions = [
    ...(dmlEntries || []).filter(e => (e.entry_type || 'dm') === 'pm' && e.pm_code).map(e => ({ label: `${e.pm_code} — ${e.title}`, pmc: e.pm_code, title: e.title })),
    ...(dirFiles || []).filter(f => f.name.toUpperCase().startsWith('PMC-')).map(f => {
      const base = f.name.replace(/\.(xml|XML)$/, '');
      return { label: base, pmc: base, title: '' };
    }),
  ];

  const handleQuickSelect = (pmc) => {
    // PMC format: PMC-MIC-ISSUER-NUMBER-VOLUME
    const match = pmc.match(/PMC-([^-_]+)-([^-_]+)-([^-_]+)-([^-_.]+)/i);
    if (match) {
      const entry = allPmcOptions.find(o => o.pmc === pmc);
      onChange({
        ...item,
        modelIdentCode: match[1],
        pmIssuer: match[2],
        pmNumber: match[3],
        pmVolume: match[4],
        pmTitle: entry?.title || item.pmTitle || '',
      });
    }
  };

  return (
    <div className="flex items-start gap-2 group" data-testid={`pm-pmref-${item.id}`}>
      <BookOpen className="w-4 h-4 text-violet-500 mt-2.5 flex-shrink-0" />
      <div className="flex-1 space-y-1.5">
        <div className="flex items-center gap-2">
          <span className="text-[10px] font-bold uppercase tracking-wider text-violet-600">pmRef</span>
          {allPmcOptions.length > 0 && (
            <select
              className="text-xs border border-slate-200 rounded px-1.5 py-0.5 bg-white max-w-xs"
              value=""
              onChange={(e) => { if (e.target.value) handleQuickSelect(e.target.value); }}
              data-testid={`pmref-quick-select-${item.id}`}
            >
              <option value="">Select from DML / directory...</option>
              {allPmcOptions.map((o, i) => (
                <option key={i} value={o.pmc}>{o.label}</option>
              ))}
            </select>
          )}
        </div>
        <div className="grid grid-cols-4 gap-1.5">
          <Input className="h-7 text-xs font-mono" placeholder="modelIdentCode" value={item.modelIdentCode || ''}
            onChange={e => onChange({ ...item, modelIdentCode: e.target.value })} />
          <Input className="h-7 text-xs font-mono" placeholder="pmIssuer" value={item.pmIssuer || ''}
            onChange={e => onChange({ ...item, pmIssuer: e.target.value })} />
          <Input className="h-7 text-xs font-mono" placeholder="pmNumber" value={item.pmNumber || ''}
            onChange={e => onChange({ ...item, pmNumber: e.target.value })} />
          <Input className="h-7 text-xs font-mono" placeholder="pmVolume" value={item.pmVolume || ''}
            onChange={e => onChange({ ...item, pmVolume: e.target.value })} />
        </div>
        <Input className="h-7 text-xs" placeholder="PM Title (optional)" value={item.pmTitle || ''}
          onChange={e => onChange({ ...item, pmTitle: e.target.value })} />
      </div>
      <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-red-400 hover:text-red-600 opacity-0 group-hover:opacity-100"
        onClick={onDelete}><Trash2 className="w-3.5 h-3.5" /></Button>
    </div>
  );
};

const ExternalPubRefEditor = ({ item, onChange, onDelete }) => (
  <div className="flex items-start gap-2 group" data-testid={`pm-extref-${item.id}`}>
    <ExternalLink className="w-4 h-4 text-amber-500 mt-2.5 flex-shrink-0" />
    <div className="flex-1 space-y-1.5">
      <span className="text-[10px] font-bold uppercase tracking-wider text-amber-600">externalPubRef</span>
      <div className="grid grid-cols-2 gap-1.5">
        <Input className="h-7 text-xs font-mono" placeholder="externalPubCode" value={item.pubCode || ''}
          onChange={e => onChange({ ...item, pubCode: e.target.value })} />
        <Input className="h-7 text-xs" placeholder="externalPubTitle" value={item.pubTitle || ''}
          onChange={e => onChange({ ...item, pubTitle: e.target.value })} />
      </div>
    </div>
    <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-red-400 hover:text-red-600 opacity-0 group-hover:opacity-100"
      onClick={onDelete}><Trash2 className="w-3.5 h-3.5" /></Button>
  </div>
);

/* ═══════════════════════════════════════════
   CHILD RENDERER (avoids recursive component)
   ═══════════════════════════════════════════ */
const ChildRenderer = ({ child, idx, onUpdateChild, onDeleteChild, depth, dmlEntries, dirFiles }) => {
  if (child.type === 'dmRef') {
    return <DmRefEditor item={child} onChange={u => onUpdateChild(idx, u)} onDelete={() => onDeleteChild(idx)} dmlEntries={dmlEntries} dirFiles={dirFiles} />;
  }
  if (child.type === 'pmRef') {
    return <PmRefEditor item={child} onChange={u => onUpdateChild(idx, u)} onDelete={() => onDeleteChild(idx)} dmlEntries={dmlEntries} dirFiles={dirFiles} />;
  }
  if (child.type === 'externalPubRef') {
    return <ExternalPubRefEditor item={child} onChange={u => onUpdateChild(idx, u)} onDelete={() => onDeleteChild(idx)} />;
  }
  if (child.type === 'pmEntry') {
    // Use React.createElement to avoid Babel plugin infinite recursion on recursive JSX
    return React.createElement(PmEntryEditor, {
      entry: child,
      onUpdate: u => onUpdateChild(idx, u),
      onDelete: () => onDeleteChild(idx),
      depth: depth + 1,
      dmlEntries: dmlEntries,
      dirFiles: dirFiles,
    });
  }
  return null;
};

/* ═══════════════════════════════════════════
   PM ENTRY EDITOR (recursive via ChildRenderer)
   ═══════════════════════════════════════════ */
const PmEntryEditor = ({ entry, onUpdate, onDelete, depth, dmlEntries, dirFiles }) => {
  const [expanded, setExpanded] = useState(true);

  const updateChild = (idx, updated) => {
    const children = [...(entry.children || [])];
    children[idx] = updated;
    onUpdate({ ...entry, children });
  };

  const deleteChild = (idx) => {
    const children = (entry.children || []).filter((_, i) => i !== idx);
    onUpdate({ ...entry, children });
  };

  const addChild = (type) => {
    const children = [...(entry.children || [])];
    if (type === 'dmRef') {
      children.push({ id: uid(), type: 'dmRef', modelIdentCode: '', systemDiffCode: '', systemCode: '', subSystemCode: '', subSubSystemCode: '', assyCode: '', disassyCode: '', disassyCodeVariant: '', infoCode: '', infoCodeVariant: '', itemLocationCode: '', dmTitle: '' });
    } else if (type === 'pmRef') {
      children.push({ id: uid(), type: 'pmRef', modelIdentCode: '', pmIssuer: '', pmNumber: '', pmVolume: '', pmTitle: '' });
    } else if (type === 'externalPubRef') {
      children.push({ id: uid(), type: 'externalPubRef', pubCode: '', pubTitle: '' });
    } else if (type === 'pmEntry') {
      children.push({ id: uid(), type: 'pmEntry', title: '', children: [] });
    }
    onUpdate({ ...entry, children });
  };

  const indentClass = depth > 0 ? 'ml-6 border-l-2 border-slate-200 pl-3' : '';

  return (
    <div className={`${indentClass} space-y-2`} data-testid={`pm-entry-${entry.id}`}>
      <div className="flex items-center gap-2 group">
        <button onClick={() => setExpanded(!expanded)} className="p-0.5 hover:bg-slate-100 rounded">
          {expanded ? <ChevronDown className="w-4 h-4 text-slate-500" /> : <ChevronRight className="w-4 h-4 text-slate-500" />}
        </button>
        <FolderTree className="w-4 h-4 text-emerald-600 flex-shrink-0" />
        <span className="text-[10px] font-bold uppercase tracking-wider text-emerald-700">pmEntry</span>
        <Input
          className="h-7 text-sm flex-1 font-medium"
          placeholder="pmEntryTitle..."
          value={entry.title || ''}
          onChange={e => onUpdate({ ...entry, title: e.target.value })}
          data-testid={`pm-entry-title-${entry.id}`}
        />
        <span className="text-[10px] text-slate-400">
          {(entry.children || []).length} item{(entry.children || []).length !== 1 ? 's' : ''}
        </span>
        <Button variant="ghost" size="sm"
          className="h-7 w-7 p-0 text-red-400 hover:text-red-600 opacity-0 group-hover:opacity-100"
          onClick={onDelete}
          data-testid={`pm-entry-delete-${entry.id}`}
        >
          <Trash2 className="w-3.5 h-3.5" />
        </Button>
      </div>

      {expanded && (
        <div className="space-y-3 ml-6">
          {(entry.children || []).map((child, idx) => (
            <ChildRenderer key={child.id} child={child} idx={idx}
              onUpdateChild={updateChild} onDeleteChild={deleteChild}
              depth={depth} dmlEntries={dmlEntries} dirFiles={dirFiles} />
          ))}

          <div className="flex gap-2 flex-wrap">
            <Button variant="ghost" size="sm" className="text-[10px] h-6 gap-1 text-blue-600"
              onClick={() => addChild('dmRef')} data-testid={`pm-add-dmref-${entry.id}`}>
              <Plus className="w-2.5 h-2.5" /> dmRef
            </Button>
            <Button variant="ghost" size="sm" className="text-[10px] h-6 gap-1 text-violet-600"
              onClick={() => addChild('pmRef')} data-testid={`pm-add-pmref-${entry.id}`}>
              <Plus className="w-2.5 h-2.5" /> pmRef
            </Button>
            <Button variant="ghost" size="sm" className="text-[10px] h-6 gap-1 text-emerald-600"
              onClick={() => addChild('pmEntry')} data-testid={`pm-add-pmentry-${entry.id}`}>
              <Plus className="w-2.5 h-2.5" /> pmEntry
            </Button>
            <Button variant="ghost" size="sm" className="text-[10px] h-6 gap-1 text-amber-600"
              onClick={() => addChild('externalPubRef')} data-testid={`pm-add-extref-${entry.id}`}>
              <Plus className="w-2.5 h-2.5" /> externalPubRef
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};

/* ═══════════════════════════════════════════
   PM EDITOR (top-level)
   ═══════════════════════════════════════════ */
export const PmEditor = ({ pmEntries, onPmEntriesChange, dmlEntries, dirFiles }) => {
  const addEntry = () => {
    const entries = [...(pmEntries || [])];
    entries.push({ id: uid(), type: 'pmEntry', title: '', children: [] });
    onPmEntriesChange(entries);
  };

  const updateEntry = (idx, updated) => {
    const entries = [...(pmEntries || [])];
    entries[idx] = updated;
    onPmEntriesChange(entries);
  };

  const deleteEntry = (idx) => {
    const entries = (pmEntries || []).filter((_, i) => i !== idx);
    onPmEntriesChange(entries);
  };

  return (
    <div className="space-y-4" data-testid="pm-editor">
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-bold text-slate-700 uppercase tracking-wider">Publication Module Content</h3>
        <Button size="sm" className="gap-1.5 h-7 bg-emerald-600 hover:bg-emerald-700 text-xs"
          onClick={addEntry} data-testid="add-pm-entry-btn">
          <Plus className="w-3 h-3" /> Add pmEntry
        </Button>
      </div>

      {(pmEntries || []).length === 0 ? (
        <div className="border-2 border-dashed border-slate-200 rounded-lg p-8 text-center">
          <FolderTree className="w-8 h-8 text-slate-300 mx-auto mb-2" />
          <p className="text-sm text-slate-500">No publication entries yet.</p>
          <p className="text-xs text-slate-400 mt-1">Click "Add pmEntry" to start building the publication structure.</p>
        </div>
      ) : (
        <div className="space-y-4">
          {pmEntries.map((entry, idx) => (
            <div key={entry.id} className="border border-slate-200 rounded-lg p-3 bg-white">
              <PmEntryEditor
                entry={entry}
                onUpdate={u => updateEntry(idx, u)}
                onDelete={() => deleteEntry(idx)}
                depth={0}
                dmlEntries={dmlEntries}
                dirFiles={dirFiles}
              />
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

/* ═══════════════════════════════════════════
   PM XML PARSER
   ═══════════════════════════════════════════ */
export function parsePmXml(doc) {
  const contentEl = doc.querySelector('content');
  if (!contentEl) return { pmEntries: [] };
  const pmEntries = [];
  for (const child of contentEl.children) {
    if (child.tagName === 'pmEntry') {
      pmEntries.push(parseSinglePmEntry(child));
    }
  }
  return { pmEntries };
}

function parseSinglePmEntry(pmEntryEl) {
  const title = pmEntryEl.querySelector(':scope > pmEntryTitle')?.textContent?.trim() || '';
  const children = [];
  for (const child of pmEntryEl.children) {
    const tag = child.tagName;
    if (tag === 'pmEntryTitle') continue;
    if (tag === 'dmRef') {
      const dmRefIdent = child.querySelector('dmRefIdent');
      const dmCode = dmRefIdent?.querySelector('dmCode');
      const tnText = child.querySelector('dmRefAddressItems dmTitle techName')?.textContent?.trim() || '';
      const inText = child.querySelector('dmRefAddressItems dmTitle infoName')?.textContent?.trim() || '';
      const dmTitle = inText ? `${tnText} - ${inText}` : tnText;
      children.push({
        id: uid(), type: 'dmRef',
        modelIdentCode: dmCode?.getAttribute('modelIdentCode') || '',
        systemDiffCode: dmCode?.getAttribute('systemDiffCode') || '',
        systemCode: dmCode?.getAttribute('systemCode') || '',
        subSystemCode: dmCode?.getAttribute('subSystemCode') || '',
        subSubSystemCode: dmCode?.getAttribute('subSubSystemCode') || '',
        assyCode: dmCode?.getAttribute('assyCode') || '',
        disassyCode: dmCode?.getAttribute('disassyCode') || '',
        disassyCodeVariant: dmCode?.getAttribute('disassyCodeVariant') || '',
        infoCode: dmCode?.getAttribute('infoCode') || '',
        infoCodeVariant: dmCode?.getAttribute('infoCodeVariant') || '',
        itemLocationCode: dmCode?.getAttribute('itemLocationCode') || '',
        dmTitle,
      });
    } else if (tag === 'pmRef') {
      const pmRefIdent = child.querySelector('pmRefIdent');
      const pmCode = pmRefIdent?.querySelector('pmCode');
      const pmTitle = child.querySelector('pmRefAddressItems pmTitle')?.textContent?.trim() || '';
      children.push({
        id: uid(), type: 'pmRef',
        modelIdentCode: pmCode?.getAttribute('modelIdentCode') || '',
        pmIssuer: pmCode?.getAttribute('pmIssuer') || '',
        pmNumber: pmCode?.getAttribute('pmNumber') || '',
        pmVolume: pmCode?.getAttribute('pmVolume') || '',
        pmTitle,
      });
    } else if (tag === 'externalPubRef') {
      const ident = child.querySelector('externalPubRefIdent');
      const pubCode = ident?.querySelector('externalPubCode')?.textContent?.trim() || '';
      const pubTitle = ident?.querySelector('externalPubTitle')?.textContent?.trim() || '';
      children.push({ id: uid(), type: 'externalPubRef', pubCode, pubTitle });
    } else if (tag === 'pmEntry') {
      children.push(parseSinglePmEntry(child));
    }
  }
  return { id: uid(), type: 'pmEntry', title, children };
}

export default PmEditor;
